import { useCallback, useEffect, useState } from "react";

/**
 * Real "add to home screen", not a line of text asking the guest to find it.
 *
 * Three worlds, and the button has to be honest about which one it's in:
 *  • Chromium fires `beforeinstallprompt`, which we must capture and keep — it
 *    can only be used once, and only from a user gesture, so it is stashed here
 *    and fired on tap.
 *  • iOS Safari never fires it and offers no programmatic install at all, so the
 *    only truthful thing to show is the manual Share → Add to Home Screen route.
 *  • Already installed (launched from the tile) — offering to install again is
 *    noise, so the caller renders nothing.
 *
 * Returns `mode`: "prompt" | "manual" | "installed" | "none".
 */
export default function useInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    const standalone =
      (typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(display-mode: standalone)").matches) ||
      // iOS marks an installed web app here rather than via display-mode.
      (typeof navigator !== "undefined" && navigator.standalone === true);
    if (standalone) setInstalled(true);

    const onBeforeInstall = (e) => {
      // Chrome shows its own mini-infobar unless we take control of the event.
      e.preventDefault();
      setDeferred(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };

    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const isIos =
    typeof navigator !== "undefined" &&
    /iphone|ipad|ipod/i.test(navigator.userAgent || "") &&
    !/crios|fxios/i.test(navigator.userAgent || "");

  const promptInstall = useCallback(async () => {
    if (!deferred) return false;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      // The event is single-use whatever they chose; holding a spent one would
      // leave a button that silently does nothing.
      setDeferred(null);
      return choice?.outcome === "accepted";
    } catch (_) {
      setDeferred(null);
      return false;
    }
  }, [deferred]);

  const mode = installed ? "installed" : deferred ? "prompt" : isIos ? "manual" : "none";

  return { mode, promptInstall };
}
